<template>
  <Accordion #default="{ isOpen, handleFocus }">
    <AccordionSection
      :path="'peddrew.mp3'"
      :open="isOpen == 0"
      @open="handleFocus(0)"
    >
      <template #title>
        Irving Peddrew III
      </template>
      <template #content>
        <p v-if="captions" class="content">
          Hello and welcome to Old Turner Street. Formerly the home of Virginia
          Tech’s Engineering Department, this site holds a special place for the
          first Black students at Virginia Tech. Instead of being free to select
          their major like their white peers, the first Black students at
          Virginia Tech were only permitted to enroll if they majored in
          engineering.
        </p>
        <p v-if="captions" class="content">
          In 1953, Irving Peddrew III of Hampton, Virginia became the first
          Black student to enroll at Virginia Tech. Here you can see an article
          from the Roanoke Times discussing the groundbreaking decision.
        </p>
        <ImageComponent :image="irving1" />
        <p v-if="captions" class="content">
          Not only was Peddrew the first Black student admitted to Virginia
          Tech, he was also the first Black undergraduate admitted to any
          four-year public University in the former 11 states of the
          Confederacy.
        </p>
        <ImageComponent :image="confederacy" />
        <p v-if="captions" class="content">
          However, as you can read from the article in front of you, Peddrew was
          admitted as a day military student and was prohibited from living on
          campus and utilizing many student facilities. As the article reads,
          “the applicant is legally entitled to admission under the particular
          and specific facts and circumstance which in this instance it is found
          to exist.”
        </p>
      </template>
    </AccordionSection>
    <AccordionSection
      :path="'students.mp3'"
      :open="isOpen == 1"
      @open="handleFocus(1)"
    >
      <template #title>
        Engineering Students
      </template>
      <template #content>
        <p v-if="captions" class="content">
          Included in these specific circumstances, were academic restrictions
          on courses of study. For Black people, they would only be admitted to
          Virginia Tech if their intended major was not offered at Virginia
          State, Virginia’s historically Black land-grant university. This meant
          that each of the first eight Black students to attend Virginia Tech
          all had to major in engineering, which was one of the few programs
          Virginia State did not offer. It was not until the 1960’s that such
          academic restrictions were lifted.
        </p>

        <hr />
        <ImageComponent :image="yates" />
        <p v-if="captions" class="content">
          Let’s hear from Dr. Charlie Yates, Virginia Tech’s first Black
          graduate, about how such restrictions affected his decision to major
          in mechanical engineering:
        </p>
        <AudioBar :path="'charles.mp3'" :startTime="0" />
        <p v-if="captions" class="content">
          <i>
            “Well, there was no question what we were studying, engineering
            because that was the only condition that we could enroll in VPI, was
            to study engineering. I didn't know a lot about engineering, so I
            decided that I had been working in this automobile dealership all
            these years that mechanical engineering had to be the right thing
            for me. So I chose mechanical engineering. I didn't really have any
            firm occupational goals at that time...”
          </i>
        </p>
        <hr />

        <ImageComponent :image="whitehurst" />
        <p v-if="captions" class="content">
          For class of 1963 graduate James Whitehurst, this engineering
          requirement actually completely altered what he studied in college.
          Determined to receive the best possible education, Whitehurst, came to
          Virginia Tech because, as he recalls, Tech had "better laboratories,
          better professors, and better equipment." Despite not being permitted
          to major in physics, Whitehurst understood that majoring in electrical
          engineering would grant him access to more academic resources and a
          higher level of education than was available at Virginia State.
        </p>
        <p v-if="captions" class="content">
          Despite this requirement and other academic restrictions, many of
          these students went on to lead long and successful careers within
          their respective industries. Ranging from NASA engineers to Virginia
          Tech professors, these students made the most of their opportunities,
          and their successes helped prove to administrators that Black students
          could succeed just like their white counterparts.
        </p>
        <ImageComponent :image="yatesgraduation" />
      </template>
    </AccordionSection>
    <AccordionSection
      :path="'trailblazers.mp3'"
      :open="isOpen == 2"
      @open="handleFocus(2)"
    >
      <template #title>
        Trailblazers
      </template>
      <template #content>
        <p v-if="captions" class="content">
          Now, we’d like to give you the opportunity to learn more about these
          trailblazing students. By clicking on the photos you see around you,
          you can get more information about their time as students and their
          careers after.
        </p>
        <i>Click on each image to learn more about that student</i>
        <br />
        <h1>Irving Peddrew III</h1>
        <InteractiveImage
          :image="'2/Irving Peddrew III Section/irving peddrew, 1953.jpg'"
          :audio="'irving.mp3'"
          :text="irvingText"
        />
        <InteractiveImage
          :image="'2/Irving Peddrew III Section/Peddrew at commencement.jpg'"
          :text="irving_postgrad"
        />
        <InteractiveImage
          :image="'2/Irving Peddrew III Section/VT admites first Negro (1).jpg'"
          :text="
            'In September of 1953, Irving Peddrew III of Hampton, VA became the first black student admitted to Virginia Tech, one of over 3,322 students. Majoring in electrical engineering, Peddrew was permitted to take classes, but was categorized as a “day military student” and was prohibited from living or eating on campus. Although Peddrew recalls no overt instances of racism on campus, he described a strong undertone of exclusion. After 3 years of carrying the burden of disregarding a school, Peddrew left Virginia Tech without graduating. '
          "
        />
        <InteractiveImage
          :image="'2/Irving Peddrew III Section/Copy of Peddrew Bowling.jpg'"
          :text="
            'Although forced to live off campus, Peddrew does recall that many fellow cadets accepted him, as you can see from the image in front of you showing Peddrew bowling with some classmates. However, his participation in student life at the school was restricted by the administration. When white students requested him as a roommate for his sophomore year, that request was disallowed. He said he could not count how many times he heard from white students, “You know, Irv, I’d like to take you home, and my parents would love to have you as a visitor, but it’s the neighbors.” '
          "
        />
        <br />
        <h1>Charlie Yates</h1>
        <InteractiveImage
          :image="'2/Charlie Yates Section/charles yates, 1958 (1).jpg'"
          :text="
            'When asked if he wished he had the opportunity to live in a dorm, Yates responded: “Well, there were positives, and there were negatives. The positives were that it did allow us direct access to the African American community. If we were living on campus, we would not have had this because living as cadets, there were certain restrictions about when you can go and when you have to come back. Also, the fact we were living off campus instead of on, we did not have all the responsibilities that a cadet would normally have. But there were also some negative aspects--getting together to study with my regular classmates. We were able to do that a lot, but not always at a convenient time because we were not living on campus.” '
          "
          :audio="'yates.mp3'"
        />
        <InteractiveImage
          :image="'2/Charlie Yates Section/charles yates old.jpg'"
          :text="
            'After graduating, Yates went on to earn his masters degree from California Tech and a Ph.D. from Johns Hopkins. Dr. Yates returned to Virginia Tech in 1979 to serve on the faculty of the Department of Mechanical Engineering (1979-1983). He left Tech in 1983 to head the Engineering Program at Hampton University, and later he was a visiting associate professor at Old Dominion University. During this period away from Tech, he served on the Virginia Tech Board of Visitors. Dr Yates returned to Tech in July of 1987 as an associate professor in the department of Aerospace and Ocean Engineering. '
          "
        />
        <InteractiveImage
          :image="
            '2/Charlie Yates Section/Charles Yates at Graduation, 1958  (1).jpg'
          "
          :text="
            'Charlie Lee Yates was the first African-American Student to graduate from Virginia Tech. He was one of six honors graduates in mechanical engineering in 1958. A graduate of Booker T. Washington High school in Norfolk, he enrolled in the Engineering School and the Corps of Cadets in 1954. He not only graduated with honors in four years, but also served as an officer in two engineering groups on campus, Tau Beta Pi and Pi Tau Sigma.'
          "
        />
        <br />
        <h1>Floyd Wilson</h1>
        <InteractiveImage
          :image="
            '2/Floyd Wilson Section/Floyd Wilson, Irving Peddrew and Charles Yates .jpg'
          "
          :text="
            'Unfortunately, we do not have much information on Floyd Wilson. He left Virginia Tech after just one year to pursue a career in the military, choosing to join the AirForce.'
          "
        />
        <br />
        <h1>Lindsay Cherry</h1>
        <InteractiveImage
          :image="'2/Lindsay Cherry Section/Lindsay Cherry 1957.jpg'"
          :text="
            '“Well, we did not associate too much with the kids on campus. There was a very small black community here. I went to church often and sang in the choir over on Clay Street. People were very nice, though, a very quiet community. It was a different type of environment from that of the city, and I really enjoyed living up here in these hills. I became a hillbilly.”'
          "
        />
        <InteractiveImage
          :image="
            '2/Lindsay Cherry Section/CHARLES YATES, ESSEX FINNEY, LINDSAY CHERRY AND MATTHEW WINSTON SR. IN 1999.jpg'
          "
          :text="
            'Lindsay Cherry was forced to leave school because of significant problems with his eyes, which prevented him from reading. Following several eye operations, he was drafted in 1960 as an enlisted man and managed a field house in Korea. After the death of Martin Luther King, Jr., in 1968, Cherry redefined himself and transformed his life by deciding to have goals and working with computers. He worked as a systems engineer for the New York Postal Data Center.'
          "
        />
        <br />
        <h1>Matthew Winston Sr.</h1>
        <InteractiveImage
          :image="'2/Matthew Winston Sr. Section/matthew winston sr, 1959.jpg'"
          :text="
            'When asked what first comes to mind when he hears the words “Virginia Tech” Winston answered as follows: “Actually now it\'s pride. It has not always been that. There were times in the past when I have been asked, “Would you do it again?”; and I have said, “Absolutely not.” Not that anything really bad happened to me, but the isolation then and the realization now that something bad could have happened at any time caused me to say no I wouldn\'t have done it had I known I was going to go through that. But, when I consider how Virginia Tech evolved through the years since I left and the example that it has set for defining a true University not only in Virginia but across the South, I am indeed proud to be a Hokie.”'
          "
          :audio="'winston.mp3'"
        />
        <InteractiveImage
          :image="'2/Matthew Winston Sr. Section/Old Winston and boys.jpg'"
          :text="
            'After graduating with a BS in Mechanical Engineering; in 1959, Mr. Winston had a distinguished career as a researcher for NASA, serving for over 35 years. He was awarded an agency medal for his contributions to equal opportunity employment within NASA.'
          "
        />
        <br />
        <h1>Essex Finney</h1>
        <InteractiveImage
          :image="'2/Essex Finney Section/Essex Finney as Cadet, 1959.jpg'"
          :text="
            '“Of course there were social activities on the campus that we could not participate in. I believe on one or two occasions we went to see President Newman to discuss the desire to participate. He was very gentlemanly, but he was also very firm that we could not do that. And, I guess, once he said that was not possible, we went back to 306 East Clay Street and went back to work, doing our academic studies.”'
          "
          :audio="'finney.mp3'"
        />
        <InteractiveImage
          :image="
            '2/Essex Finney Section/Essex Finney with Mr. and Mrs. Hoge, 1959 .jpg'
          "
          :text="
            'After completing his degree in Agricultural Engineering in 1959, he earned his master\'s from Penn State and his doctorate from Michigan State. Dr. Finney was a Princeton Fellow in Public Affairs in the Woodrow Wilson School, Princeton University in 1973-74. He served from July 1980 to June 1981 in the Office of the Science Advisor to the President in Washington, D. C., in the administrations of both Jimmy Carter and Ronald Reagan. In 1985 the College of Engineering of Pennsylvania State presented Dr. Finney with an Outstanding Engineering Alumnus Award in recognition of his expertise in agricultural research, for his contributions to enhancing the quality of our Nation\'s food and agricultural products, and for his invaluable activity within professional agricultural organizations. Dr. Finney worked for many years in the Agricultural Research Service (ARS) of the United States Department of Agriculture. In 1993 he was appointed Acting ARS Administrator.'
          "
        />
        <br />
        <h1>James Whitehurst</h1>
        <InteractiveImage
          :image="
            '2/James Whitehurst Section/James Whitehurst on Board of Visitors, 1972 (1).jpg'
          "
          :text="
            'Following his graduation in 1963, Whitehurst enlisted in the Airforce and served as a fighter pilot in the Vietnam War. He later became a major in the Air Force Reserve in Virginia and a member of the Air National Guard. He was the first Black member of the Virginia Tech Board of Visitors (1970–74). He received his Juris Doctorate from the University of Virginia in 1975. He served on the University of Virginia Law Counsel and operated a private law practice in Richmond.\n Whitehurst broke many racial barriers as a student and alumnus of Virginia Tech. In 1961, he successfully pressured the administration into allowing Black students to live on campus. In 1962, Whitehurst became the first Black student to attend Virginia Tech’s honored tradition of Ring Dance, something all previous Black students had been prohibited from attending. In 2020, Barringer Hall was renamed Whitehurst Hall to honor his achievements and tremendous impact on Virginia Tech. \nIn 1970, Whitehurst became the first Black member of the Virginia Tech Board of Visitors. He served until 1974.'
          "
        />
      </template>
    </AccordionSection>
  </Accordion>
</template>

<script>
import Accordion from "@/components/Accordion.vue";
import AccordionSection from "@/components/AccordionSection.vue";
import AudioBar from "@/components/AudioBar.vue";
import ImageComponent from "@/components/ImageComponent.vue";
import InteractiveImage from "../InteractiveImage.vue";

export default {
  name: "HillcrestAccordion",
  components: {
    Accordion,
    AccordionSection,
    AudioBar,
    ImageComponent,
    InteractiveImage
  },
  props: {
    captions: {
      type: Boolean
    }
  },
  data() {
    return {
      irving1: {
        name: "Irving Peddrew III, 1958.",
        path: "Irving Peddrew III Section/VT admites first Negro (1).jpg",
        caption:
          "A 1953 Roanoke Times article covering the admission of Irving Peddrew III, the first Black student to enroll at Virginia Tech."
      },
      confederacy: {
        name: "Confederate States of America",
        path: "Opening photos/confederate states.jpg",
        caption: "A map of the former Confederate States of America."
      },
      yates: {
        name: "Charles Yates, 1958",
        path: "Opening photos/charles yates, 1958.jpg",
        caption:
          "A photo of Charles Yates, the first Black graduate of Virginia Tech, 1958."
      },
      whitehurst: {
        name: "James Whitehurst on Board",
        path:
          "James Whitehurst Section/James Whitehurst on Board of Visitors, 1972 (1).jpg",
        caption: "James Whitehurst on the Board of Visitors, 1972."
      },
      yatesgraduation: {
        name: "Charles Yates at Graduation",
        path:
          "Charlie Yates Section/Charles Yates at Graduation, 1958  (1).jpg",
        caption: "Charles Yates at Graduation, 1958."
      },
      irvingText:
        "“I was more of a social being, I needed to interact with the people around me. Although they weren't the overt acts of discrimination or segregation on campus, I still felt that loneliness and some isolation that's hard to describe. It's so difficult that I couldn't, if you paid me a million dollars, I don't think that I could adequately describe how I felt. I've tried to reflect on it, particularly since Tech has asked me to come and be a part of the fiftieth-year commemorative events. I have racked my brain. I couldn't, I can't tell you what it meant to me internally. What it did to me, and how it affected me, and how it was good for me to get away finally... There was that undertone, that thing that was always there, and although I was equipped to come in and deal with it for a year, eventually it took its toll.”",
      irving_postgrad:
        "After a trip to California as part of the YMCA-sponsored Students in Vocation project, Peddrew did not return to VPI for his senior year. He decided to continue his education in California. He enrolled in some courses at Santa Monica City College and took acting classes in Los Angeles. He got a job as a draftsman for an aerospace firm, Summers Gyroscope. He was drafted by the army in 1958, trained at the National Security Agency, and served in Berlin. After his military service he worked as a designer in the aerospace industry, graduated from the Columbia School of Broadcasting, and later worked as a technical service representative with expertise in bananas and pineapples first for Dole (Castle & Cooke Inc.) and then for Del Monte. In 2003, the University passed a resolution renaming New Residence Hall West as Peddrew-Yates Hall. In 2016, Irving Peddrew III was presented with an honorary degree in electrical engineering at the University’s Commencement Ceremony. "
    };
  }
};
</script>

<style lang="scss" scoped>
h1 {
  color: white;
  font-size: 1.2em;
}
i {
  color: silver;
}
</style>
